import accountService from '@/services/account';
import invoicesService from '@/services/invoices';
import customersService from '@/services/customers';
import paymentReceiptsService from '@/services/paymentReceipts';
import generalCatalogsService from '@/services/generalCatalogs';
import financialAccountRepository from '@/services/financialAccountRepository';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { mapSerialOption, normalizeFiscalCodeOptions } from '@/composables/invoices/create/helpers';
import {
  buildPaymentAccountOptions,
  extractPayModalBankData,
  resolveDefaultPaymentAccount,
  resolveDefaultTransferPaymentMethod,
} from '@/composables/pay/usePayModalShared';

const resolveToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeVoucherType = (value = 'cfdi') => String(value || 'cfdi').trim().toLowerCase();

const normalizeCurrencyCode = (value = 'MXN') => String(value || 'MXN').trim().toUpperCase() || 'MXN';

const resolveNumericValue = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolvePendingAmountByCurrency = (source = {}, currency = 'MXN') => {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  const directMatch = resolveNumericValue(source?.pendingByCurrency?.[normalizedCurrency]);
  if (directMatch > 0) {
    return directMatch;
  }

  const candidateValues = [source?.pendingAmount, source?.pending_amount, source?.pending, source?.balance];

  for (let index = 0; index < candidateValues.length; index += 1) {
    const resolved = resolveNumericValue(candidateValues[index]);
    if (resolved > 0) {
      return resolved;
    }
  }

  return 0;
};

const resolveCreatedPaymentReceiptId = (responsePayload = {}) => {
  const id = Number(responsePayload?.payment_receipt?.id || 0) || 0;
  return id > 0 ? id : 0;
};

const mapCustomerPendingSelectorOption = (item = {}) => {
  const pendingSource = item?.pending_by_currency && typeof item.pending_by_currency === 'object'
    ? item.pending_by_currency
    : {};

  const pendingByCurrency = Object.entries(pendingSource).reduce((accumulator, [currency, amount]) => {
    const normalizedCurrency = normalizeCurrencyCode(currency);
    accumulator[normalizedCurrency] = resolveNumericValue(amount);
    return accumulator;
  }, {});

  return {
    value: Number(item?.customer_id || item?.id || 0),
    label: String(item?.customer_name || item?.name || '').trim(),
    meta: {
      id: Number(item?.customer_id || item?.id || 0),
      customerId: Number(item?.customer_id || item?.id || 0),
      pendingByCurrency,
      pendingAmount: resolveNumericValue(item?.pending_amount),
    },
  };
};

const mapContributorOptionByCustomer = (item = {}, customerLabel = '') => ({
  value: Number(item?.id || 0),
  label: String(item?.name || '').trim(),
  customerId: Number(item?.company_id || 0),
  meta: {
    id: Number(item?.id || 0),
    customerId: Number(item?.company_id || 0),
    default: Number(item?.default || 0),
    clientName: String(customerLabel || '').trim(),
    rfc: String(item?.rfc || '').trim(),
    businessName: String(item?.name || '').trim(),
    cfdiUseCode: String(item?.uso_cfdi || '').trim(),
    cfdiUse: String(item?.uso_cfdi_label || item?.uso_cfdi || '').trim(),
    taxRegimeCode: String(item?.regimen || '').trim(),
    taxRegime: String(item?.regimen_label || item?.regimen || '').trim(),
    fiscalResidence: String(item?.zipcode || '').trim(),
    beneficiaryBankRfc: String(item?.rfc_banco_beneficiario || '').trim(),
    beneficiaryAccountNumber: String(item?.payment_account || '').trim(),
    orderingBank: String(item?.nom_banco_ord_ext || '').trim(),
    orderingBankRfc: String(item?.rfc_emisor_cta_ord || '').trim(),
    orderingBankAccount: String(item?.cta_ordenante || '').trim(),
  },
});

const resolveDocumentTypeFromRow = (row = {}) => {
  const voucherType = normalizeVoucherType(
    row?.voucherType
    || row?.voucher_type
    || row?.type
  );

  if (voucherType === 'simple_note') {
    return 'simple_note';
  }

  return 'cfdi';
};

const mapPendingDocument = (row = {}, index = 0) => {
  const rawId = row?.id || row?.payment_plan_id || '';
  const numericId = Number(rawId || 0) || 0;
  const id = String(rawId || '').trim() !== '' ? rawId : (numericId > 0 ? numericId : `temp-${index}`);
  const total = resolveNumericValue(row?.total || row?.amount || row?.subtotal);
  const pendingAmount = resolveNumericValue(
    row?.pendingAmount
    || row?.pending_amount
    || row?.pending
    || row?.balance
    || row?.remaining
  );

  return {
    id,
    paymentId: Number(row?.payment_id || row?.paymentId || 0) || 0,
    reference: String(row?.reference || row?.folio || row?.invoice || row?.document || '').trim(),
    date: String(row?.date || row?.invoice_date || row?.document_date || '').trim(),
    type: resolveDocumentTypeFromRow(row),
    typeLabel: String(row?.typeLabel || row?.voucher_type_label || row?.voucherTypeLabel || '').trim(),
    paymentMethodCode: String(row?.payment_method_code || row?.paymentMethodCode || '').trim(),
    invoiceId: Number(row?.invoice_id || row?.invoiceId || 0) || 0,
    saleId: Number(row?.sale_id || row?.saleId || 0) || 0,
    saleCode: String(row?.sale_code || row?.saleCode || '').trim(),
    businessName: String(row?.business_name || row?.businessName || '').trim(),
    businessLabel: String(row?.business_label || row?.businessLabel || '').trim(),
    concept: String(row?.concept || '').trim(),
    concepts: Array.isArray(row?.concepts) ? row.concepts : [],
    total,
    pendingAmount,
    appliedAmount: 0,
    isLiquidated: false,
    currency: normalizeCurrencyCode(row?.currency || 'MXN'),
  };
};

const rebalanceApplicationAllocations = (vm) => {
  const maxPayableAmount = Math.max(0, Number(vm?.info?.receivedAmount || 0) || 0);
  const rows = Array.isArray(vm?.pendingDocuments) ? vm.pendingDocuments : [];

  const normalizedRows = rows.map((row) => {
    const pendingAmount = Math.max(0, Number(row?.pendingAmount || 0) || 0);
    const appliedAmount = Math.max(0, Math.min(Number(row?.appliedAmount || 0) || 0, pendingAmount));

    return {
      ...row,
      pendingAmount,
      appliedAmount,
      isLiquidated: Boolean(row?.isLiquidated),
    };
  });

  const manualTotal = normalizedRows.reduce((accumulator, row) => {
    if (row.isLiquidated) {
      return accumulator;
    }

    return accumulator + row.appliedAmount;
  }, 0);

  let availableForLiquidation = Math.max(0, maxPayableAmount - manualTotal);

  vm.pendingDocuments = normalizedRows.map((row) => {
    if (!row.isLiquidated) {
      return row;
    }

    const nextAppliedAmount = Math.max(0, Math.min(row.pendingAmount, availableForLiquidation));
    availableForLiquidation -= nextAppliedAmount;

    return {
      ...row,
      appliedAmount: nextAppliedAmount,
    };
  });

  vm.totalAmount = (Array.isArray(vm.pendingDocuments) ? vm.pendingDocuments : [])
    .reduce((accumulator, row) => accumulator + (Number(row?.appliedAmount || 0) || 0), 0);
};

export const createPaymentReceiptCreateState = (text = {}) => ({
  headerActions: [
    {
      key: 'back',
      label: text?.create?.headerActions?.back || 'Regresar',
      icon: 'fa-arrow-left',
      variant: 'secondary',
    },
  ],
  currency: 'MXN',
  exchangeRate: 1,
  submitting: false,
  issuer: {
    businessName: '',
    rfc: '',
    taxRegime: '',
    fiscalResidence: '',
  },
  customerOptions: [],
  selectedCustomer: null,
  contributorOptions: [],
  selectedReceiver: null,
  taxRegimeOptions: [],
  cfdiUseOptions: [],
  paymentMethodTypeOptions: [],
  paymentFormOptions: [],
  paymentAccountOptions: [],
  serialOptions: [],
  info: {
    voucherType: 'cfdi',
    date: resolveToday(),
    serialId: '',
    series: '',
    folio: text?.create?.ui?.infoCard?.emptyValue || '-',
    cfdiFolio: text?.create?.ui?.infoCard?.emptyValue || '-',
    paymentForm: '',
    operationNumber: '',
    paymentAccountId: '',
    receivedAmount: 0,
  },
  bankDetails: {
    expanded: false,
    beneficiaryBankRfc: '',
    beneficiaryAccountNumber: '',
    orderingBank: '',
    orderingBankRfc: '',
    orderingBankAccount: '',
  },
  customerPendingAmount: 0,
  pendingDocuments: [],
  loadingPendingDocuments: false,
  totalAmount: 0,
  showSubmitConfirmModal: false,
  submitConfirmMode: 'normal',
});

export const paymentReceiptCreateComputed = {
  documentTypeOptions() {
    return [
      {
        value: 'cfdi',
        label: this.text.create.ui.infoCard.voucherTypes.cfdi,
      },
      {
        value: 'simple_note',
        label: this.text.create.ui.infoCard.voucherTypes.simpleNote,
      },
    ];
  },
  isCfdiMode() {
    return String(this.info?.voucherType || 'cfdi').trim().toLowerCase() !== 'simple_note';
  },
  filteredCustomerOptions() {
    return Array.isArray(this.customerOptions) ? this.customerOptions : [];
  },
  applicationDocuments() {
    const type = normalizeVoucherType(this.info?.voucherType);
    const currency = normalizeCurrencyCode(this.currency);
    const rows = Array.isArray(this.pendingDocuments) ? this.pendingDocuments : [];

    return rows.filter((row) => {
      const rowType = normalizeVoucherType(row?.type || 'cfdi');
      const rowCurrency = normalizeCurrencyCode(row?.currency || currency);
      return rowType === type && rowCurrency === currency;
    });
  },
  disableSubmitCfdi() {
    return !this.isCfdiMode;
  },
  appliedTotalAmount() {
    return (Array.isArray(this.applicationDocuments) ? this.applicationDocuments : [])
      .reduce((accumulator, row) => accumulator + (Number(row?.appliedAmount || 0) || 0), 0);
  },
  selectedApplicationItems() {
    return (Array.isArray(this.applicationDocuments) ? this.applicationDocuments : [])
      .filter((row) => Number(row?.paymentId || 0) > 0)
      .filter((row) => Number(row?.appliedAmount || 0) > 0);
  },
  submitSummary() {
    const customerName = String(this.selectedCustomer?.label || '').trim();
    const voucherTypeLabel = this.isCfdiMode
      ? String(this.text?.create?.ui?.infoCard?.voucherTypes?.cfdi || 'CFDI').trim()
      : String(this.text?.create?.ui?.infoCard?.voucherTypes?.simpleNote || 'Nota simple').trim();

    const paymentFormCode = String(this.info?.paymentForm || '').trim();
    const paymentFormMatch = (Array.isArray(this.paymentFormOptions) ? this.paymentFormOptions : [])
      .find((option) => String(option?.value || '').trim() === paymentFormCode);

    const paymentAccountId = String(this.info?.paymentAccountId || '').trim();
    const paymentAccountMatch = (Array.isArray(this.paymentAccountOptions) ? this.paymentAccountOptions : [])
      .find((option) => String(option?.value || '').trim() === paymentAccountId);

    const receiverMeta = this.selectedReceiver?.meta || this.selectedReceiver || {};

    return {
      customerName,
      voucherTypeLabel,
      paymentInfo: {
        date: String(this.info?.date || '').trim(),
        paymentForm: paymentFormCode,
        paymentFormLabel: String(paymentFormMatch?.label || paymentFormCode || '').trim(),
        paymentAccount: paymentAccountId,
        paymentAccountLabel: String(paymentAccountMatch?.label || paymentAccountId || '').trim(),
        operationNumber: String(this.info?.operationNumber || '').trim(),
        banks: {
          orderingBank: String(this.bankDetails?.orderingBank || '').trim(),
          orderingBankRfc: String(this.bankDetails?.orderingBankRfc || '').trim(),
          orderingBankAccount: String(this.bankDetails?.orderingBankAccount || '').trim(),
          beneficiaryBankRfc: String(this.bankDetails?.beneficiaryBankRfc || '').trim(),
          beneficiaryAccountNumber: String(this.bankDetails?.beneficiaryAccountNumber || '').trim(),
        },
        fiscalInfo: this.isCfdiMode
          ? {
            rfc: String(receiverMeta?.rfc || '').trim(),
            businessName: String(receiverMeta?.businessName || receiverMeta?.name || '').trim(),
            taxRegime: String(receiverMeta?.taxRegime || receiverMeta?.taxRegimeCode || '').trim(),
            cfdiUse: String(receiverMeta?.cfdiUse || receiverMeta?.cfdiUseCode || '').trim(),
            postalCode: String(receiverMeta?.fiscalResidence || '').trim(),
          }
          : null,
      },
      items: this.selectedApplicationItems.map((row) => ({
        id: String(row?.id || ''),
        reference: String(row?.reference || '').trim(),
        business: (() => {
          const backendBusinessLabel = String(row?.businessLabel || row?.business_label || '').trim();
          if (backendBusinessLabel !== '') {
            return backendBusinessLabel;
          }

          const saleCode = String(row?.saleCode || '').trim();
          const businessName = String(row?.businessName || '').trim();

          if (saleCode !== '' && businessName !== '') {
            return `${saleCode} - ${businessName}`;
          }

          if (saleCode !== '') {
            return saleCode;
          }

          if (businessName !== '') {
            return businessName;
          }

          return 'Sin negocio relacionado';
        })(),
        concept: String(row?.concept || '').trim(),
        amount: Number(row?.appliedAmount || 0) || 0,
      })),
      totalAmount: this.appliedTotalAmount,
    };
  },
};

export const paymentReceiptCreateMethods = {
  async initializePage() {
    await Promise.all([
      this.loadIssuerTaxInformation(),
      this.loadCustomerPendingSelectors(),
      this.loadCatalogs(),
    ]);
  },
  async loadIssuerTaxInformation() {
    try {
      const response = await accountService.getTaxInformation();
      const payload = extractPayload(response);
      const data = payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object'
        ? payload.data
        : payload;

      const businessName = String(data?.business_name || data?.name || '').trim();
      const rfc = String(data?.rfc || '').trim();

      if (businessName === '' && rfc === '') {
        return;
      }

      this.issuer = {
        ...this.issuer,
        businessName: businessName !== '' ? businessName : this.issuer.businessName,
        rfc: rfc !== '' ? rfc : this.issuer.rfc,
        taxRegime: String(data?.regimen_label || data?.regimen || '').trim() || this.issuer.taxRegime,
        fiscalResidence: String(data?.fiscal_residence || data?.zipcode || '').trim() || this.issuer.fiscalResidence,
      };
    } catch (error) {
      // Keep fallback issuer values when endpoint is unavailable.
    }
  },
  async loadCustomerPendingSelectors() {
    try {
      const response = await paymentReceiptsService.customerPendingSelectors({
        voucher_type: this.info?.voucherType || 'cfdi',
        currency: this.currency,
      });
      const mapped = extractCollection(response)
        .map((item) => mapCustomerPendingSelectorOption(item))
        .filter((option) => Number(option?.value || 0) > 0)
        .filter((option) => resolvePendingAmountByCurrency(option?.meta || option, this.currency) > 0);

      this.customerOptions = mapped;

      const selectedCustomerStillExists = mapped.some(
        (option) => Number(option?.value || 0) === Number(this.selectedCustomer?.value || 0)
      );

      if (!selectedCustomerStillExists) {
        this.selectedCustomer = null;
        this.selectedReceiver = null;
        this.contributorOptions = [];
      } else {
        const nextSelectedCustomer = mapped.find(
          (option) => Number(option?.value || 0) === Number(this.selectedCustomer?.value || 0)
        ) || null;

        this.selectedCustomer = nextSelectedCustomer;
      }
    } catch (error) {
      this.customerOptions = [];
      this.selectedCustomer = null;
      this.selectedReceiver = null;
      this.contributorOptions = [];
    }
  },
  async loadContributorsByCustomer(customerOption = null) {
    const customerId = Number(customerOption?.value || this.selectedCustomer?.value || 0);
    if (customerId <= 0) {
      this.contributorOptions = [];
      this.selectedReceiver = null;
      return;
    }

    try {
      const response = await customersService.contributorsIndex(customerId);
      const rows = extractCollection(response);
      const customerLabel = String(customerOption?.label || this.selectedCustomer?.label || '').trim();

      const mapped = rows
        .map((row) => mapContributorOptionByCustomer(row, customerLabel))
        .filter((option) => Number(option?.value || 0) > 0);

      this.contributorOptions = mapped;

      const selected = mapped.find((option) => Number(option?.meta?.default || 0) === 1)
        || mapped[0]
        || null;

      this.selectedReceiver = selected;
      this.prefillBankData(selected);
    } catch (error) {
      this.contributorOptions = [];
      this.selectedReceiver = null;
    }
  },
  async loadCatalogs() {
    const results = await Promise.allSettled([
      generalCatalogsService.getRegimens(),
      generalCatalogsService.getUsosCfdi(),
      generalCatalogsService.getMetodosPago(),
      generalCatalogsService.getFormasPago(),
      financialAccountRepository.selectors(),
      invoicesService.serialSelectors({ document_type: 'P' }),
    ]);

    const regimensResult = results[0];
    const usosCfdiResult = results[1];
    const metodosPagoResult = results[2];
    const formasPagoResult = results[3];
    const paymentAccountsResult = results[4];
    const serialsResult = results[5];

    this.taxRegimeOptions = regimensResult?.status === 'fulfilled'
      ? normalizeFiscalCodeOptions(extractCollection(regimensResult.value))
      : [];

    this.cfdiUseOptions = usosCfdiResult?.status === 'fulfilled'
      ? normalizeFiscalCodeOptions(extractCollection(usosCfdiResult.value))
      : [];

    this.paymentMethodTypeOptions = metodosPagoResult?.status === 'fulfilled'
      ? normalizeFiscalCodeOptions(extractCollection(metodosPagoResult.value))
      : [];

    this.paymentFormOptions = formasPagoResult?.status === 'fulfilled'
      ? normalizeFiscalCodeOptions(extractCollection(formasPagoResult.value))
      : [];

    const defaultTransfer = resolveDefaultTransferPaymentMethod(this.paymentFormOptions);
    if (defaultTransfer) {
      this.info = {
        ...this.info,
        paymentForm: String(defaultTransfer?.value || '').trim(),
      };
    }

    if (paymentAccountsResult?.status === 'fulfilled') {
      this.paymentAccountOptions = buildPaymentAccountOptions(
        extractCollection(paymentAccountsResult.value),
        this.paymentsText?.payModal?.banking?.unassignedAccount || 'Sin cuenta asignada'
      );

      const defaultPaymentAccount = resolveDefaultPaymentAccount(this.paymentAccountOptions);
      this.info = {
        ...this.info,
        paymentAccountId: String(defaultPaymentAccount?.value || '').trim(),
      };
    } else {
      this.paymentAccountOptions = [];
      this.info = {
        ...this.info,
        paymentAccountId: '',
      };
    }

    if (serialsResult?.status !== 'fulfilled') {
      this.serialOptions = [];
      return;
    }

    const mappedSerials = extractCollection(serialsResult.value)
      .map((item) => mapSerialOption(item))
      .filter((option) => Number(option?.value || 0) > 0 && String(option?.label || '').trim() !== '');

    this.serialOptions = mappedSerials;

    if (mappedSerials.length === 0) {
      return;
    }

    const firstSerial = mappedSerials[0];
    const selectedSeries = String(firstSerial?.label || '').trim();
    const selectedFolio = String(firstSerial?.meta?.folio ?? '').trim();

    this.info = {
      ...this.info,
      serialId: firstSerial?.value || '',
      series: selectedSeries,
      folio: selectedFolio || this.text.create.ui.infoCard.emptyValue,
      cfdiFolio: `${selectedSeries}${selectedFolio}`.trim() || this.text.create.ui.infoCard.emptyValue,
    };
  },
  async selectCustomer(customer) {
    this.selectedCustomer = customer && typeof customer === 'object' ? customer : null;

    if (this.isCfdiMode) {
      await this.loadContributorsByCustomer(this.selectedCustomer);
    } else {
      this.selectedReceiver = null;
      this.contributorOptions = [];
    }

    this.syncCustomerPendingAmount();
    await this.loadPendingDocumentsForSelection();
  },

  async selectReceiver(receiver) {
    this.selectedReceiver = receiver && typeof receiver === 'object' ? receiver : null;

    if (!this.isCfdiMode) {
      return;
    }

    this.prefillBankData(this.selectedReceiver);
    await this.loadPendingDocumentsForSelection();
  },
  prefillBankData(receiver) {
    const bankData = extractPayModalBankData(receiver || {});

    this.bankDetails = {
      ...this.bankDetails,
      ...bankData,
    };
  },
  async onInfoChange(payload) {
    const key = String(payload?.key || '').trim();
    if (key === '') {
      return;
    }

    this.info = {
      ...this.info,
      [key]: payload?.value,
    };

    if (key === 'receivedAmount') {
      rebalanceApplicationAllocations(this);
    }

    if (key === 'voucherType') {
      const isCfdi = String(payload?.value || '').trim().toLowerCase() !== 'simple_note';
      if (!isCfdi) {
        this.bankDetails = {
          ...this.bankDetails,
          expanded: false,
        };
      }

      this.customerPendingAmount = 0;

      await this.loadCustomerPendingSelectors();

      if (!isCfdi) {
        this.selectedReceiver = null;
      } else if (this.selectedCustomer) {
        await this.loadContributorsByCustomer(this.selectedCustomer);
      }

      this.syncCustomerPendingAmount();
      await this.loadPendingDocumentsForSelection();
    }
  },
  async onCurrencyChange(currency) {
    this.currency = normalizeCurrencyCode(currency);
    this.customerPendingAmount = 0;
    await this.loadCustomerPendingSelectors();
    this.syncCustomerPendingAmount();
    await this.loadPendingDocumentsForSelection();
  },
  onExchangeRateChange(rate) {
    const parsed = Number(rate || 0);
    this.exchangeRate = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  },
  async loadPendingDocumentsForSelection() {
    this.loadingPendingDocuments = true;

    const customerId = Number(this.selectedCustomer?.value || 0);
    if (customerId <= 0) {
      this.pendingDocuments = [];
      this.totalAmount = 0;
      this.loadingPendingDocuments = false;
      return;
    }

    try {
      const response = await paymentReceiptsService.applicationItems({
        customer_id: customerId,
        voucher_type: this.info?.voucherType || 'cfdi',
        currency: this.currency,
        contributor_id: Number(this.selectedReceiver?.value || this.selectedReceiver?.meta?.id || 0) || 0,
        contributor_rfc: String(this.selectedReceiver?.meta?.rfc || this.selectedReceiver?.rfc || '').trim(),
      });

      const rows = extractCollection(response);
      this.pendingDocuments = rows.map((row, index) => mapPendingDocument(row, index));
      rebalanceApplicationAllocations(this);
    } catch (error) {
      this.pendingDocuments = [];
      this.totalAmount = 0;
    } finally {
      this.loadingPendingDocuments = false;
    }
  },
  syncCustomerPendingAmount() {
    if (!this.selectedCustomer) {
      this.customerPendingAmount = 0;
      return;
    }

    const source = this.selectedCustomer?.meta || this.selectedCustomer;
    this.customerPendingAmount = resolvePendingAmountByCurrency(source, this.currency);
  },
  onApplicationAmountChange(payload) {
    const id = payload?.id;
    const amount = Number(payload?.amount || 0) || 0;

    const currentDocuments = Array.isArray(this.pendingDocuments) ? this.pendingDocuments : [];
    const currentReceivedAmount = Math.max(0, Number(this.info?.receivedAmount || 0) || 0);

    const otherRowsAppliedTotal = currentDocuments.reduce((accumulator, row) => {
      if (String(row?.id) === String(id)) {
        return accumulator;
      }

      return accumulator + (Number(row?.appliedAmount || 0) || 0);
    }, 0);

    const maxAllowedByGeneralAmount = Math.max(0, currentReceivedAmount - otherRowsAppliedTotal);

    const nextDocuments = currentDocuments.map((row) => {
      if (String(row?.id) !== String(id)) {
        return row;
      }

      const maxAmount = Number(row?.pendingAmount || 0) || 0;
      const nextAppliedAmount = Math.max(0, Math.min(amount, maxAmount, maxAllowedByGeneralAmount));

      return {
        ...row,
        appliedAmount: nextAppliedAmount,
        isLiquidated: false,
      };
    });

    this.pendingDocuments = nextDocuments;

    rebalanceApplicationAllocations(this);
  },
  onToggleApplicationLiquidate(payload = {}) {
    const id = payload?.id;
    const shouldLiquidate = Boolean(payload?.value);

    if (shouldLiquidate && Number(this.info?.receivedAmount || 0) <= 0) {
      return;
    }

    this.pendingDocuments = (Array.isArray(this.pendingDocuments) ? this.pendingDocuments : []).map((row) => {
      if (String(row?.id) !== String(id)) {
        return row;
      }

      return {
        ...row,
        isLiquidated: shouldLiquidate,
        appliedAmount: shouldLiquidate ? (Number(row?.appliedAmount || 0) || 0) : 0,
      };
    });

    rebalanceApplicationAllocations(this);
  },
  onBankDetailsChange(nextBankDetails) {
    this.bankDetails = {
      ...this.bankDetails,
      ...(nextBankDetails || {}),
    };
  },
  onHeaderAction(action) {
    const actionKey = String(action?.key || '').trim();
    if (actionKey !== 'back') {
      return;
    }

    this.goBack();
  },
  async onEditContributor() {
    await this.loadContributorsByCustomer(this.selectedCustomer);
    await this.loadPendingDocumentsForSelection();
    this.$toast?.success('Dato fiscal actualizado correctamente.');
  },
  openRelatedCfdiSelector() {
    this.$toast?.info(String(this.text?.create?.ui?.conceptsCard?.toasts?.cfdiRelatedPending || ''));
  },
  validateBeforeSubmit(shouldStamp = false) {
    if (Number(this.info?.serialId || 0) <= 0) {
      this.$toast?.error(String(this.text?.create?.validation?.serialRequired || ''));
      return false;
    }

    if (!this.selectedCustomer) {
      this.$toast?.error('Selecciona un cliente para registrar el pago.');
      return false;
    }

    if (this.isCfdiMode && !this.selectedReceiver) {
      this.$toast?.error(String(this.text?.create?.validation?.receiverRequired || ''));
      return false;
    }

    if (shouldStamp && !this.isCfdiMode) {
      this.$toast?.error(String(this.text?.create?.validation?.cfdiRequired || ''));
      return false;
    }

    if (this.selectedApplicationItems.length === 0) {
      this.$toast?.error(String(this.text?.create?.validation?.applicationRequired || ''));
      return false;
    }

    if (this.appliedTotalAmount <= 0) {
      this.$toast?.error(String(this.text?.create?.validation?.amountRequired || ''));
      return false;
    }

    const declaredAmount = Math.max(0, Number(this.info?.receivedAmount || 0) || 0);
    const appliedAmount = Math.max(0, Number(this.appliedTotalAmount || 0) || 0);
    if (declaredAmount > 0 && Math.abs(appliedAmount - declaredAmount) > 0.009999) {
      this.$toast?.warning(
        String(
          this.text?.create?.validation?.appliedAmountMismatch
          || 'Aun faltan importes por aplicar para completar el monto del pago. Revisa la aplicacion antes de continuar.'
        )
      );
      return false;
    }

    return true;
  },
  buildPayload(shouldStamp = false) {
    const customerId = Number(this.selectedCustomer?.value || this.selectedCustomer?.meta?.customerId || 0) || 0;
    const contributorId = Number(
      this.selectedReceiver?.meta?.id
      || this.selectedReceiver?.value
      || this.selectedReceiver?.id
      || 0
    ) || 0;

    return {
      document_type: 'P',
      voucher_type: String(this.info?.voucherType || 'cfdi').trim().toLowerCase(),
      date: String(this.info?.date || '').trim(),
      serial_id: Number(this.info?.serialId || 0) || 0,
      payment_form: String(this.info?.paymentForm || '').trim(),
      operation_number: String(this.info?.operationNumber || '').trim(),
      payment_account_id: Number(this.info?.paymentAccountId || 0) || 0,
      amount: Number(this.appliedTotalAmount || 0) || 0,
      currency: String(this.currency || 'MXN').trim().toUpperCase() || 'MXN',
      exchange_rate: Number(this.exchangeRate || 1) || 1,
      customer_id: customerId,
      contributor_id: contributorId,
      items: this.selectedApplicationItems.map((row) => ({
        payment_id: Number(row?.paymentId || 0) || 0,
        amount: Number(row?.appliedAmount || 0) || 0,
      })),
      beneficiary_bank_rfc: String(this.bankDetails?.beneficiaryBankRfc || '').trim(),
      beneficiary_account_number: String(this.bankDetails?.beneficiaryAccountNumber || '').trim(),
      ordering_bank: String(this.bankDetails?.orderingBank || '').trim(),
      ordering_bank_rfc: String(this.bankDetails?.orderingBankRfc || '').trim(),
      ordering_bank_account_number: String(this.bankDetails?.orderingBankAccount || '').trim(),
      should_stamp_payment: shouldStamp ? 1 : 0,
      stamp_payment: shouldStamp ? 1 : 0,
      timbrar: shouldStamp ? 1 : 0,
    };
  },
  openSubmitConfirmation(shouldStamp = false) {
    this.submitConfirmMode = shouldStamp ? 'cfdi' : 'normal';
    this.showSubmitConfirmModal = true;
  },
  closeSubmitConfirmation() {
    if (this.submitting) {
      return;
    }

    this.showSubmitConfirmModal = false;
  },
  async confirmSubmitPayment() {
    if (this.submitting) {
      return;
    }

    const shouldStamp = String(this.submitConfirmMode || '').trim().toLowerCase() === 'cfdi';
    if (!this.validateBeforeSubmit(shouldStamp)) {
      return;
    }

    const payload = this.buildPayload(shouldStamp);
    this.submitting = true;

    try {
      const response = await paymentReceiptsService.store(payload);
      const apiErrorMessage = extractApiErrorMessage(response, '');
      if (apiErrorMessage !== '') {
        this.$toast?.error(apiErrorMessage);
        return;
      }

      const responsePayload = extractPayload(response);
      const createdPaymentReceiptId = resolveCreatedPaymentReceiptId(responsePayload);
      const successMessage = extractApiSuccessMessage(
        response,
        String(this.text?.create?.toast?.submitSuccess || 'Pago registrado correctamente.')
      );

      this.$toast?.success(successMessage);
      this.showSubmitConfirmModal = false;

      if (createdPaymentReceiptId > 0) {
        this.$router.push({
          name: 'paymentReceiptDetail',
          params: {
            paymentReceiptId: String(createdPaymentReceiptId),
          },
        }).catch(() => {});
        return;
      }

      this.goBack();
    } catch (error) {
      const message = String(error?.response?.data?.message || '').trim()
        || String(error?.message || '').trim()
        || String(this.text?.create?.toast?.submitError || 'No fue posible registrar el pago.');
      this.$toast?.error(message);
    } finally {
      this.submitting = false;
    }
  },
  submitPayment() {
    if (!this.validateBeforeSubmit(false)) {
      return;
    }

    this.openSubmitConfirmation(false);
  },
  submitPaymentCfdi() {
    if (!this.validateBeforeSubmit(true)) {
      return;
    }

    this.openSubmitConfirmation(true);
  },
  goBack() {
    this.$router.push({ name: 'paymentReceipts' });
  },
};
